import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { useEffect, useState } from 'react';
import { useOnlineStatus } from './useOnlineStatus';

interface OfflineDB extends DBSchema {
  'obligaciones': {
    key: string;
    value: any;
  };
  'documentos': {
    key: string;
    value: any;
  };
  'cumplimientos': {
    key: string;
    value: any;
  };
  'evidencias': {
    key: string;
    value: any;
  };
  'pending_operations': {
    key: string;
    value: {
      id: string;
      operation: 'create' | 'update' | 'delete';
      table: string;
      data: any;
      timestamp: number;
    };
  };
}

const DB_NAME = 'viabiliza_offline_db';
const DB_VERSION = 1;

let dbInstance: IDBPDatabase<OfflineDB> | null = null;

const initDB = async (): Promise<IDBPDatabase<OfflineDB>> => {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<OfflineDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Create stores if they don't exist
      if (!db.objectStoreNames.contains('obligaciones')) {
        db.createObjectStore('obligaciones');
      }
      if (!db.objectStoreNames.contains('documentos')) {
        db.createObjectStore('documentos');
      }
      if (!db.objectStoreNames.contains('cumplimientos')) {
        db.createObjectStore('cumplimientos');
      }
      if (!db.objectStoreNames.contains('evidencias')) {
        db.createObjectStore('evidencias');
      }
      if (!db.objectStoreNames.contains('pending_operations')) {
        db.createObjectStore('pending_operations');
      }
    },
  });

  return dbInstance;
};

export const useOfflineStorage = () => {
  const [db, setDb] = useState<IDBPDatabase<OfflineDB> | null>(null);
  const isOnline = useOnlineStatus();

  useEffect(() => {
    initDB().then(setDb).catch(console.error);
  }, []);

  const cacheData = async (store: keyof OfflineDB, key: string, data: any) => {
    if (!db) return;
    try {
      await db.put(store as any, data, key);
    } catch (error) {
      console.error(`Error caching data in ${store}:`, error);
    }
  };

  const getCachedData = async (store: keyof OfflineDB, key?: string) => {
    if (!db) return null;
    try {
      if (key) {
        return await db.get(store as any, key);
      } else {
        return await db.getAll(store as any);
      }
    } catch (error) {
      console.error(`Error getting cached data from ${store}:`, error);
      return null;
    }
  };

  const addPendingOperation = async (
    operation: 'create' | 'update' | 'delete',
    table: string,
    data: any
  ) => {
    if (!db) return;
    try {
      const id = `${Date.now()}-${Math.random()}`;
      await db.put('pending_operations', {
        id,
        operation,
        table,
        data,
        timestamp: Date.now(),
      }, id);
    } catch (error) {
      console.error('Error adding pending operation:', error);
    }
  };

  const getPendingOperations = async () => {
    if (!db) return [];
    try {
      return await db.getAll('pending_operations');
    } catch (error) {
      console.error('Error getting pending operations:', error);
      return [];
    }
  };

  const clearPendingOperation = async (id: string) => {
    if (!db) return;
    try {
      await db.delete('pending_operations', id);
    } catch (error) {
      console.error('Error clearing pending operation:', error);
    }
  };

  const clearAllPendingOperations = async () => {
    if (!db) return;
    try {
      await db.clear('pending_operations');
    } catch (error) {
      console.error('Error clearing all pending operations:', error);
    }
  };

  const clearAllData = async (store: keyof OfflineDB) => {
    if (!db) return;
    try {
      await db.clear(store as any);
    } catch (error) {
      console.error(`Error clearing ${store}:`, error);
    }
  };

  const getAllKeys = async (store: keyof OfflineDB) => {
    if (!db) return [];
    try {
      return await db.getAllKeys(store as any);
    } catch (error) {
      console.error(`Error getting keys from ${store}:`, error);
      return [];
    }
  };

  return {
    cacheData,
    getCachedData,
    addPendingOperation,
    getPendingOperations,
    clearPendingOperation,
    clearAllPendingOperations,
    clearAllData,
    getAllKeys,
    isOnline,
  };
};
